import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { Button, Chip, Icon, Tooltip, formatNumber } from '@column/column-ui-kit';

import { StatusFilterSelect } from '../_components/StatusFilterSelect';
import { ROUTE } from '~/app/routes';
import { BankAccountCard, CopyInput, FileUpload, Loading, RenderFields, SectionHeader } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { isACHBatchAllFailed } from '~/lib/transfers';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { AchBatchRepository } from '~/repositories/Transfer/AchBatchRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner, Text } from '~/styles';
import { ACHTransferBatch, BankAccountWithDetails } from '~/typings/API';

import { AchBatchHeader } from './_components/AchBatchHeader';
import { AchBatchTransfersTable, STATUS_FILTER_OPTIONS } from './_components/AchBatchTransfersTable';
import { ConfirmBatchModal } from './_components/ConfirmBatchModal';
import {
  getBatchStatusDisplay,
  getCreateTransfersPermissionMessage,
  hasCreatableTransfers,
} from './_components/transferHelpers';

const POLL_MS = 5000;

const TightDetails = styled.div`
  > div {
    padding-top: 0;
    padding-bottom: 24px;
  }
`;

const NON_TERMINAL_BATCH = new Set(['pending', 'validating', 'validated', 'processing']);

const CANCELABLE_STATUSES = new Set(['pending', 'validating', 'validated']);

// Unsent batches resume in the create-transfer form instead of this read-only view.
const RESUMABLE_STATUSES = new Set(['pending', 'validating', 'validated']);

const CREATE_TRANSFERS_TOOLTIP = 'Create the validated transfers in this batch.';

const STATEMENT_GROUPING: Record<string, { label: string; description: string }> = {
  individual: {
    label: 'Individual',
    description: 'List each transfer separately on the statement.',
  },
  rolled_up: {
    label: 'Rolled up',
    description: 'Combine into one credit and one debit line per day.',
  },
};

// Aligns the option name's first line with the field Label's 12px top margin (side-by-side layout).
const GroupingValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  @container form-field (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    margin-top: 12px;
  }
`;

interface Params {
  id: string;
}

export const PageTransfersAchBatchView: React.FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const isAuthorized = useSessionStore((s) => s.isAuthorized);
  const formMethods = useForm();
  const queryClient = useQueryClient();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirmPending, setIsConfirmPending] = useState(false);
  const [transferStatusFilter, setTransferStatusFilter] = useState<string>('');

  const batchQueryKey = ['ach-batch', platformId, isSandbox, id];

  const navigate = useNavigate();
  const { data: batch, isLoading: isBatchLoading } = useQuery<ACHTransferBatch>({
    queryKey: batchQueryKey,
    queryFn: () => AchBatchRepository.get({ id }),
    enabled: !!id,
    refetchInterval: ({ state }) => (NON_TERMINAL_BATCH.has(state.data?.status ?? '') ? POLL_MS : false),
  });

  const willRedirectToResume = !!batch && RESUMABLE_STATUSES.has(batch.status);
  useEffect(() => {
    if (willRedirectToResume) {
      navigate(`${ROUTE.TRANSFERS}/batches/edit/ach-batch?batchId=${id}`, { replace: true });
    }
  }, [willRedirectToResume, id, navigate]);

  const cancelMutation = useMutation({
    mutationFn: () => AchBatchRepository.cancel(id, uuid()),
    onSuccess: () => {
      addSuccessNotification({ content: 'Import canceled' });
      queryClient.invalidateQueries({ queryKey: batchQueryKey });
    },
    onError: (error: { message?: string }) => {
      addDangerNotification({ content: error?.message ?? 'Cancel failed' });
    },
  });

  const lifecycleBusy = isConfirmPending || cancelMutation.isPending;
  const bankAccountId = batch?.importConfig?.nacha?.bankAccountId;
  const accountNumberId = batch?.importConfig?.nacha?.accountNumberId;
  const bankAccountQueryKey = [platformId, 'bank-account', bankAccountId];
  const { data: bankAccount } = useQuery<BankAccountWithDetails>({
    queryKey: bankAccountQueryKey,
    queryFn: () => BankAccountRepository.get({ id: bankAccountId ?? '' }),
    enabled: !!bankAccountId,
    gcTime: 30000,
    staleTime: 10000,
  });
  const currentBalance = bankAccount?.balances?.availableAmount;
  const currentBalanceValue = currentBalance === undefined ? '-' : formatNumber(currentBalance);

  const handleConfirmed = () => {
    queryClient.invalidateQueries({ queryKey: batchQueryKey });
    queryClient.invalidateQueries({ queryKey: bankAccountQueryKey });
  };

  const createTransfersPermissionMessage = batch
    ? getCreateTransfersPermissionMessage(batch, isAuthorized, bankAccount)
    : undefined;
  const createTransfersTooltip = createTransfersPermissionMessage ?? CREATE_TRANSFERS_TOOLTIP;

  // The header carries the status for a successfully completed batch; every other state (processing,
  // failed, canceled, expired, all-failed, …) surfaces it via the Status field below instead.
  const showHeader = !!batch && batch.status === 'completed' && !isACHBatchAllFailed(batch);

  return (
    <>
      <Loading isLoading={isBatchLoading || willRedirectToResume} fullHeight>
        {batch && !willRedirectToResume && (
          <>
            {isConfirmOpen && (
              <ConfirmBatchModal
                batch={batch}
                isOpen={isConfirmOpen}
                availableBalance={currentBalanceValue}
                onClose={() => setIsConfirmOpen(false)}
                onConfirmed={handleConfirmed}
                onPendingChange={setIsConfirmPending}
              />
            )}
            {showHeader && <AchBatchHeader batch={batch} />}
            {batch.status === 'pending' && (
              <>
                <SectionHeader text="Upload NACHA file" />
                <Inner pt={0} px="var(--page-margin)">
                  <FileUpload<ACHTransferBatch>
                    handleUpload={(file) => AchBatchRepository.uploadFile(batch.id, file)}
                    name={`ach-batch-upload-${batch.id}`}
                    description="Upload your NACHA file"
                    renderFileName={(uploaded) => <>{uploaded.id}</>}
                    onFileUpload={() => queryClient.invalidateQueries({ queryKey: batchQueryKey })}
                  />
                </Inner>
              </>
            )}
            <SectionHeader text="Batch Information" border={batch.status === 'pending' || showHeader}>
              {batch && CANCELABLE_STATUSES.has(batch.status) && (
                <Tooltip content="Discard this import. No transfers will be created." placement="bottom">
                  <Button
                    variant="secondary-danger"
                    size="small"
                    icon={<Icon.CircleCross />}
                    onClick={() => cancelMutation.mutate()}
                    isLoading={cancelMutation.isPending}
                    isDisabled={lifecycleBusy}
                  >
                    Cancel Batch
                  </Button>
                </Tooltip>
              )}
              {batch && hasCreatableTransfers(batch) && (
                <Tooltip content={createTransfersTooltip} placement="bottom">
                  <Button
                    size="small"
                    onClick={() => setIsConfirmOpen(true)}
                    isDisabled={lifecycleBusy || !!createTransfersPermissionMessage}
                  >
                    Create transfers
                  </Button>
                </Tooltip>
              )}
            </SectionHeader>
            <FormProvider {...formMethods}>
              <TightDetails>
                <RenderFields
                  sections={[
                    {
                      fields: [
                        ...(!showHeader
                          ? [
                              {
                                id: 'status',
                                label: 'Status',
                                children: () => {
                                  const status = getBatchStatusDisplay(batch);
                                  return (
                                    <Chip size="large" type={status.type} icon={status.icon}>
                                      {status.label}
                                    </Chip>
                                  );
                                },
                              },
                            ]
                          : []),
                        // The header (shown for completed batches) already carries the Batch ID in its
                        // top-right slot, so only repeat it here when the header isn't rendered.
                        ...(!showHeader
                          ? [
                              {
                                id: 'batchId',
                                label: 'Batch ID',
                                children: () => <CopyInput value={batch.id} isMonospaced />,
                              },
                            ]
                          : []),
                        {
                          id: 'description',
                          label: 'Description',
                          children: () => <CopyInput value={batch.description || '-'} />,
                        },
                        ...(bankAccountId || accountNumberId
                          ? [
                              {
                                id: 'bankAccount',
                                label: 'Bank Account',
                                children: () => (
                                  <BankAccountCard
                                    variant="detailed"
                                    bankAccountId={bankAccountId}
                                    accountNumberId={accountNumberId}
                                  />
                                ),
                              },
                            ]
                          : []),
                        {
                          id: 'importType',
                          label: 'Import Type',
                          children: () => (
                            <Chip
                              size="large"
                              icon={
                                batch.importType === 'nacha' ? (
                                  <Icon.Nacha style={{ ['--icon-size' as string]: '16px' }} />
                                ) : batch.importType === 'csv' ? (
                                  <Icon.Csv style={{ ['--icon-size' as string]: '16px' }} />
                                ) : undefined
                              }
                            >
                              {batch.importType ? batch.importType.toUpperCase() : '-'}
                            </Chip>
                          ),
                        },
                        {
                          id: 'statementGroupingMode',
                          label: 'Statement Grouping',
                          children: () => {
                            const grouping = STATEMENT_GROUPING[batch.statementGroupingMode];
                            return (
                              <GroupingValue>
                                <Text as="span" size={14} weight="medium" color="foreground">
                                  {grouping?.label ?? batch.statementGroupingMode}
                                </Text>
                                {grouping && (
                                  <Text as="span" size={12} color="light">
                                    {grouping.description}
                                  </Text>
                                )}
                              </GroupingValue>
                            );
                          },
                        },
                      ],
                    },
                  ]}
                />
              </TightDetails>
            </FormProvider>
            <SectionHeader text="Transfers" border>
              <StatusFilterSelect
                value={transferStatusFilter}
                onChange={setTransferStatusFilter}
                options={STATUS_FILTER_OPTIONS}
              />
            </SectionHeader>
            <Inner pt={0} px="var(--page-margin)">
              <AchBatchTransfersTable
                batchId={batch.id}
                batchStatus={batch.status}
                statusFilter={transferStatusFilter}
              />
            </Inner>
          </>
        )}
      </Loading>
    </>
  );
};
